import React, { FC } from 'react';
import styled from 'styled-components';
import { urlRegex } from '../../../../../regex';
import UrlLink from './UrlLink';
import { getAnchorIfExists } from '../../../../../utils';
import LinkifiedText from '../../../../../atoms/LinkifiedText';

type StatusTone = 'success' | 'warning' | 'info' | 'neutral' | 'danger';

const toneByStatus = (raw: string): StatusTone => {
  const s = (raw || '').trim().toLowerCase();
  if (!s) return 'neutral';
  if (s.includes('issued') || s.includes('approved') || s.includes('completed') || s.includes('paid')) return 'success';
  if (s.includes('progress') || s.includes('started') || s.includes('inspection')) return 'info';
  if (s.includes('pending') || s.includes('waiting') || s.includes('sent') || s.includes('hold')) return 'warning';
  if (s.includes('denied') || s.includes('rejected') || s.includes('overdue') || s.includes('emergency') || s.includes('failed')) return 'danger';
  return 'neutral';
};

const TableCell: FC<{
  header: string;
  value?: string;
  hideLabel?: boolean;
  isHtml?: boolean;
}> = ({ isHtml = false, header, value, hideLabel = false, children }) => {
  // A lone URL renders as a link chip; text containing URLs is linkified below.
  const isUrl = urlRegex.test(value) && !/\s/.test(String(value).trim());
  const isStatus = header?.toLowerCase() === 'status' && !!value;
  const isProjectName = header && /project name|permit name/i.test(header);

  return (
    <TableCellWrapper>
      <div title={value} style={{ wordWrap: 'break-word', minWidth: 0 }}>
        {isUrl ? (
          <UrlLink value={value} />
        ) : (
          <TextWrap>
            <TableCellLabel hideLabel={hideLabel}>{header}</TableCellLabel>
            {isStatus ? (
              <StatusChip $tone={toneByStatus(value)}>{value}</StatusChip>
            ) : isHtml ? (
              <div dangerouslySetInnerHTML={{ __html: getAnchorIfExists(value) }} />
            ) : (
              <CellValue $emphasize={isProjectName}>
                <LinkifiedText value={value} />
              </CellValue>
            )}
          </TextWrap>
        )}
        {children}
      </div>
    </TableCellWrapper>
  );
};

export default TableCell;

const CellValue = styled.div<{ $emphasize?: boolean }>`
  font-size: 0.875rem;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif, 'Apple Color Emoji',
    'Segoe UI Emoji', 'Segoe UI Symbol';
  color: ${({ $emphasize }) => ($emphasize ? 'var(--neutral-800)' : 'var(--mainText)')};
  font-weight: ${({ $emphasize }) => ($emphasize ? 600 : 400)};
  margin: 0;
  text-wrap: wrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TextWrap = styled.div`
  text-wrap: auto;
  min-width: 0;
`;

const TableCellWrapper = styled.div`
  padding: 12px 16px;
  color: var(--mainText);
  font-size: 0.875rem;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif, 'Apple Color Emoji',
    'Segoe UI Emoji', 'Segoe UI Symbol';
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  min-width: 0;

  @media only screen and (max-width: 840px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const TableCellLabel = styled.div<{ hideLabel: boolean }>`
  font-weight: bold;
  font-size: 0.7em;
  display: none;

  @media only screen and (max-width: 840px) {
    display: ${({ hideLabel }) => (hideLabel ? 'none' : 'block')};
    font-size: 12px;
  }
`;

const chipPalette: Record<StatusTone, { bg: string; fg: string; border: string }> = {
  success: { bg: 'var(--success-50, #e8f6ee)', fg: 'var(--success-700, #196a3b)', border: 'var(--success-100, #c5e7d2)' },
  warning: { bg: 'var(--warning-50, #fff6e0)', fg: 'var(--warning-700, #8a5f05)', border: 'var(--warning-100, #ffe7ac)' },
  info: { bg: 'var(--info-50, #e8f2fd)', fg: 'var(--info-600, #1e64bf)', border: 'var(--primary-100, #cfe6f3)' },
  danger: { bg: 'var(--danger-50, #fdecec)', fg: 'var(--danger-700, #9a2523)', border: 'var(--danger-100, #f9c9c9)' },
  neutral: { bg: 'var(--neutral-100)', fg: 'var(--neutral-700)', border: 'var(--border)' },
};

const StatusChip = styled.span<{ $tone: StatusTone }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1.4;
  white-space: nowrap;
  background: ${({ $tone }) => chipPalette[$tone].bg};
  color: ${({ $tone }) => chipPalette[$tone].fg};
  border: 1px solid ${({ $tone }) => chipPalette[$tone].border};
`;
